import CustomDataSourceFields from '../../../../types/report/CustomDataSourceFields';
import FunctionOperand from './FunctionOperand';
import IngestionConfig from '../../../../types/ingestion/IngestionConfig';
import React from 'react';
import operandType from '../../../../infrastructure/constants/functions/operandType';
import typeHelper from '../../../../infrastructure/helpers/common/typeHelper';
import operandDisplayHelper from '../../../../infrastructure/helpers/functions/operand/operandDisplayHelper';
import Lookup from '../../../../types/functions/Lookup';
import Operand from '../../../../types/functions/Operand/Operand';
import OperandDetails from '../../../../types/functions/Operand/OperandDetails';
import Dictionary from '../../../../types/report/Dictionary';
import ReportFieldDefinition from '../../../../types/report/ReportFieldDefinition';
import ReportVariable from '../../../../types/report/ReportVariable';
import getDictionaryOperand from './getDictionaryOperand';
import UserDefinedFunction from '../../../../types/report/UserDefinedFunction';
import getUserDefinedFunctionOperand from './getUserDefinedFunctionOperand';
import ReportLogicLocation from '../../../../types/functions/Location/ReportLogicLocation';
import ReportCase from '../../../../types/report/ReportCase';
import locationType from '../../../../types/functions/Location/LocationType';
import Aggregation from '../../../../types/report/Aggregation';
import mixedOperandHelper from '../../../../infrastructure/helpers/functions/operand/mixedOperandHelper';
import actions from '../../../../store/actions';
import store from '../../../../store/store';

const getOperandButtonText = (operand: OperandDetails): string => {
    if (!operand.isSetUp) {
        return '<not set>';
    }

    return operand.displayText;
};

const getOperandButtonClasses = (operand: OperandDetails): string => {
    let classes = ['transparent-button', 'code'];

    if (operand.isSetUp) {
        classes.push(operand.operandType.toLowerCase());
    }
    else {
        classes.push('instruction');
    }

    return classes.join(' ');
};

const onShowDefinitionClick = (details: OperandDetails, location: ReportLogicLocation): void => {

    store.dispatch({ type: actions.navigation.navigateToItem, payload: { details, location } });
}

const getOperandButton = (
    operand: Operand,
    tooltip: string | null,
    functions: UserDefinedFunction[],
    globalFunctions: UserDefinedFunction[],
    lookups: Lookup[],
    dictionaries: Dictionary[],
    variables: ReportVariable[],
    dataSources: IngestionConfig[],
    reportFields: ReportFieldDefinition[],
    customDataSourceFields: CustomDataSourceFields,
    onClick: (event: React.MouseEvent<HTMLElement>) => void,
    disabled: boolean,
    reportConfigType: string,
    location?: ReportLogicLocation,
    cases?: ReportCase[],
    aggregations?: Aggregation[],
    children?: JSX.Element
): JSX.Element => {

    let details = operandDisplayHelper.getOperandDetails(operand, functions, globalFunctions, lookups, dictionaries, variables, dataSources, reportFields, customDataSourceFields, reportConfigType, null, cases, location as ReportLogicLocation);

    if (operand.operandType === operandType.field && (location?.statement === locationType.statement.case || location?.statement === locationType.statement.reportField) &&
        cases && aggregations) {
        let aggregationNumber = cases.find(x => x.number === location.statementNumber)?.aggregationNumber as number;

        if (aggregationNumber) {
            details.displayText = getCaseDataSourceName(operand, details.displayText, cases, aggregations, dataSources, aggregationNumber);
        }
    }

    return (
        <span className='display-flex align-items-center'>
            <button className={getOperandButtonClasses(details)} onClick={onClick} title={tooltip as string} disabled={disabled}>
                {getOperandButtonText(details)}
            </button>
            {
                typeHelper.isString(details.definitionHtmlId) &&
                <button className="transparent-button" onClick={() => onShowDefinitionClick(details, location || {} as ReportLogicLocation)} title="Go to definition">*</button>
            }
            {children}
        </span>
    );
};

const getOperandContent = (
    operand: Operand,
    operandNumber: number,
    argumentIndexes: number[],
    tooltip: string | null,
    functions: UserDefinedFunction[],
    globalFunctions: UserDefinedFunction[],
    lookups: Lookup[],
    dictionaries: Dictionary[],
    variables: ReportVariable[],
    dataSources: IngestionConfig[],
    reportFields: ReportFieldDefinition[],
    customDataSourceFields: CustomDataSourceFields,
    selectorNumber: number | null,
    onClick: (operandNumber: number, argumentIndexes: number[], event: React.MouseEvent<HTMLElement>) => void,
    disabled: boolean,
    reportConfigType: string,
    location?: ReportLogicLocation,
    cases?: ReportCase[],
    aggregations?: Aggregation[],
    children?: JSX.Element | undefined
): JSX.Element => {

    if (operand.operandType === operandType.function) {

        return <FunctionOperand
            operand={operand}
            operandNumber={operandNumber}
            argumentIndexes={argumentIndexes}
            functions={functions}
            globalFunctions={globalFunctions}
            lookups={lookups}
            dictionaries={dictionaries}
            variables={variables}
            dataSources={dataSources}
            reportFields={reportFields}
            customDataSourceFields={customDataSourceFields}
            selectorNumber={selectorNumber}
            onClick={(operandNumber: number, argumentIndexes: number[], event: React.MouseEvent<HTMLElement>) => { onClick(operandNumber, argumentIndexes, event) }}
            disabled={disabled}
            reportConfigType={reportConfigType}
            location={location}
            cases={cases}
            aggregations={aggregations}
            children={children}
        />
    }

    if (operand.operandType === operandType.dictionary ||
        operand.operandType === operandType.dictionaryResultSet) {

        return getDictionaryOperand(operand, operandNumber, argumentIndexes, functions, globalFunctions, lookups, dictionaries, variables, dataSources, reportFields, customDataSourceFields,
            selectorNumber, (number, argIndexes, event) => { onClick(number, argIndexes, event) }, disabled, reportConfigType, children);
    }

    if (operand.operandType === operandType.userDefinedFunction || operand.operandType === operandType.globalUserDefinedFunction) {

        return getUserDefinedFunctionOperand(operand, operandNumber, argumentIndexes, functions, globalFunctions, lookups, dictionaries, variables, dataSources, reportFields, customDataSourceFields,
            selectorNumber, (number, argIndexes, event) => { onClick(number, argIndexes, event) }, disabled, reportConfigType, children);
    }

    return getOperandButton(operand, tooltip, functions, globalFunctions, lookups, dictionaries, variables, dataSources, reportFields, customDataSourceFields,
        (event) => { onClick(operandNumber, argumentIndexes, event) }, disabled, reportConfigType, location, cases, aggregations, children);
};


const getCaseDataSourceName = (operand: Operand, displayText: string, cases: ReportCase[], aggregations: Aggregation[], dataSources: IngestionConfig[], aggregationNumber: number): string => {

    let aggregation = aggregations.find(x => x.number === aggregationNumber);

    let field = operandDisplayHelper.getFieldDetails(operand, dataSources)?.field;

    let text = mixedOperandHelper.buildAggregatedCaseWithFieldIdentifier(aggregation?.name as string, field?.name as string);

    return text || displayText;
}

export {
    getOperandContent
};
